import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, type ChatSummary } from '../api/client.ts';
import { useChat } from '../chat/ChatContext.tsx';
import { ChatPicker } from '../components/ChatPicker.tsx';

/**
 * «Кому переслать» — the second and last step of forwarding a message.
 *
 * There is no third. The canvas is explicit that tapping a chat *is* the send:
 * no preview of what is about to go, no «Отправить», no confirmation. What
 * makes that safe is that the message is already on screen behind this — the
 * person chose it a moment ago from its own hold menu — so the only thing left
 * to decide is where, and a screen that asked twice would be asking about
 * something nobody has stopped looking at.
 *
 * A route rather than an overlay, and inside ChatLayout: the socket and the
 * loaded threads stay up, and Android's back button leaves the picker instead
 * of the app — the same arrangement «Новая группа» and the settings screens are
 * under.
 */
export function ForwardScreen() {
  const navigate = useNavigate();
  const { chatId, messageId } = useParams();
  const { forwardMessage } = useChat();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function forward(target: ChatSummary) {
    if (busy || !messageId) return;
    setBusy(true);
    setError(null);
    try {
      await forwardMessage(messageId, target.id);
      // replace, so the back button lands on the conversation the message came
      // from rather than on a picker for a message that has already gone.
      navigate(`/chat/${target.id}`, { replace: true });
    } catch (err) {
      // Stays on the list: the chat that was tapped is still the one they
      // meant, and the second press is the retry.
      setError(err instanceof ApiError ? err.message : 'Не удалось переслать');
      setBusy(false);
    }
  }

  return (
    <ChatPicker
      title="Кому переслать"
      hint="Нажатие на чат пересылает сообщение сразу."
      busy={busy}
      error={error}
      onPick={(chat) => void forward(chat)}
      onClose={() => navigate(chatId ? `/chat/${chatId}` : '/')}
    />
  );
}
